import { Box, Divider } from '@mui/material';
import Typography from '@mui/material/Typography';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import React, { MouseEvent } from 'react';
import {
  useLocale,
  formatArticleDate,
  formatCategoriesWithBullet,
  parseHTMLDescription,
} from '@xolv-mybrightlink/common';
import { Article } from '../models/ArticleResult';

type ArticleItemProps = {
  article: Article;
  onBookmarkClicked?: (
    event: MouseEvent<SVGSVGElement>,
    isSaved: boolean,
  ) => void;
  onArticleClicked?: () => void;
};

export const ArticleItem = React.forwardRef<HTMLDivElement, ArticleItemProps>(
  (props, ref) => {
    const locale = useLocale();
    return (
      <Box
        ref={ref}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          pt: 10,
        }}
        onClick={props.onArticleClicked}
      >
        <Box
          sx={{
            display: 'flex',
            cursor: 'pointer',
          }}
        >
          <Box
            sx={{
              position: 'relative',
            }}
          >
            <img
              src={props.article.featuredImage.node.sourceUrl}
              style={{
                width: 320,
                height: 240,
                borderRadius: 12,
                boxShadow:
                  '0 12px 16px 0 rgba(59, 69, 89, 0.05), 0 8px 10px 0 rgba(59, 69, 89, 0.1)',
                objectFit: 'cover',
              }}
              alt={props.article.title}
            />
            <Typography
              variant={'body2'}
              sx={{
                position: 'absolute',
                top: 0,
                background: '#626a7a',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px 0px 12px 0px',
              }}
            >
              {formatArticleDate(props.article.date, locale)}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              ml: 10,
              width: '100%',
            }}
          >
            <Box>
              <Typography
                sx={{
                  px: 2,
                }}
                variant={'h6'}
              >
                {props.article.title}
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                }}
              >
                <iframe
                  onClick={props.onArticleClicked}
                  style={{
                    border: 'none',
                    overflow: 'hidden',
                  }}
                  height='85%'
                  width='100%'
                  srcDoc={parseHTMLDescription(
                    props.article.content,
                    '#3b4559',
                    3,
                  )}
                  sandbox=''
                  scrolling='no'
                />
                <Box
                  sx={{
                    position: 'absolute',
                    height: '100%',
                    width: '100%',
                    top: 0,
                  }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                sx={{
                  px: 2,
                }}
                variant={'body2'}
              >
                {formatCategoriesWithBullet(props.article.categories.nodes)}
              </Typography>
              {props.article.isSaved ? (
                <BookmarkIcon
                  sx={{
                    color: '#307fe2',
                    width: 40,
                    height: 40,
                  }}
                  onClick={(event) =>
                    props.onBookmarkClicked &&
                    props.onBookmarkClicked(event, false)
                  }
                />
              ) : (
                <BookmarkBorderIcon
                  sx={{
                    color: '#307fe2',
                    width: 40,
                    height: 40,
                  }}
                  onClick={(event) =>
                    props.onBookmarkClicked &&
                    props.onBookmarkClicked(event, true)
                  }
                />
              )}
            </Box>
          </Box>
        </Box>
        <Divider
          sx={{
            pb: 10,
            background: 'transparent',
            borderColor: 'transparent',
          }}
        />
      </Box>
    );
  },
);
ArticleItem.displayName = 'ArticleItem';
